import { createConnectTransport } from "@connectrpc/connect-web";
import { createClient, type Interceptor } from "@connectrpc/connect";

import { AccountService } from "@/gen/nodebase/v1/account_pb";
import { getSession, sessionFrom, setSession } from "@/lib/session";

// Same-origin, always: nginx serves this bundle and proxies /nodebase.v1.* to
// control-api, so there is no API host to configure and no CORS to negotiate.
// A configurable base URL would only be a way to point production at the wrong
// control plane.
const BASE_URL = "/";

// The control plane speaks Connect-JSON (protojson over POST). Binary would be
// smaller, but JSON is what every existing debugging habit — curl, the browser
// network tab, the server logs — already reads.
const commonOptions = { baseUrl: BASE_URL, useBinaryFormat: false } as const;

// authTransport carries no credentials and never refreshes. The login RPCs use
// it: they are how a caller *obtains* a token, so attaching one would be
// circular, and the refresh call must not be able to re-enter the refresh
// interceptor below.
export const authTransport = createConnectTransport(commonOptions);
export const authClient = createClient(AccountService, authTransport);

// refreshInFlight collapses concurrent refreshes. Panels fire several queries at
// once; without this, each would spend the refresh token separately and all but
// one would present a token the server had already rotated away.
let refreshInFlight: Promise<string> | null = null;

// Refresh a minute early. The server offers no "expired" signal distinguishable
// from any other permission error, so the client tracks expiry itself and the
// margin covers clock skew plus the round trip.
const REFRESH_MARGIN_SEC = 60;

async function currentAccessToken(): Promise<string | null> {
  const s = getSession();
  if (!s) return null;
  if (s.accessExpiresUnix - REFRESH_MARGIN_SEC > Date.now() / 1000) {
    return s.accessToken;
  }
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await authClient.refreshSession({
          refreshToken: s.refreshToken,
        });
        const next = sessionFrom(s.email, s.accountId, res.session, s.newAccount);
        setSession(next);
        return next.accessToken;
      } catch (err) {
        // A refresh token the server will not honour cannot be recovered from;
        // dropping the session sends the app back to the login card, which is
        // the only honest thing left to show.
        setSession(null);
        throw err;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

const authInterceptor: Interceptor = (next) => async (req) => {
  const token = await currentAccessToken();
  if (token) req.header.set("Authorization", `Bearer ${token}`);
  return next(req);
};

// transport is what every authenticated call goes through.
export const transport = createConnectTransport({
  ...commonOptions,
  interceptors: [authInterceptor],
});
